import { ClerkProvider as BaseClerkProvider, useAuth } from "@clerk/clerk-react";
import { dark } from "@clerk/themes";
import { useTheme } from "@/lib/hooks/use-theme";
import { useEffect } from "react";

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;


// Session monitor component to handle session state
function SessionMonitor({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {

    // Listen for session errors
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.includes('clerk') && !e.newValue) {
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isLoaded, isSignedIn]);

  // Show loading state while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export function ClerkProvider({ children, forceTheme }: { children: React.ReactNode; forceTheme?: 'light' | 'dark' }) {
  const { theme: systemTheme } = useTheme();
  const theme = forceTheme || systemTheme;

  if (!publishableKey) {
    throw new Error("Missing publishable key");
  }

  const lightColors = {
    primary: "hsl(347, 77%, 50%)", // Rose/pink primary
    primaryLight: "hsl(347, 77%, 70%)",
    primaryDark: "hsl(347, 77%, 40%)",
    background: "hsl(0, 0%, 100%)",
    foreground: "hsl(0, 0%, 9%)",
    foregroundMuted: "hsl(0, 0%, 45%)",
    border: "hsl(0, 0%, 89%)",
  };

  const darkColors = {
    primary: "hsl(346, 100%, 60%)", // Brighter rose for dark mode
    primaryLight: "hsl(346, 100%, 70%)",
    primaryDark: "hsl(346, 100%, 50%)",
    background: "hsl(0, 0%, 9%)",
    foreground: "hsl(0, 0%, 98%)",
    foregroundMuted: "hsl(0, 0%, 63%)",
    border: "hsl(0, 0%, 18%)",
  };

  const colors = theme === "dark" ? darkColors : lightColors;

  return (
    <BaseClerkProvider
      publishableKey={publishableKey}
      appearance={{
        baseTheme: theme === "dark" ? dark : undefined,
        variables: {
          colorPrimary: colors.primary,
          colorTextOnPrimaryBackground: "#FFFFFF",
          colorBackground: colors.background,
          colorInputBackground: colors.background,
          colorInputText: colors.foreground,
          colorText: colors.foreground,
          borderRadius: "0",
        },
        elements: {
          rootBox: `bg-transparent`,
          card: `bg-card border border-border shadow-none`,
          headerTitle: `text-foreground font-bold`,
          headerSubtitle: `text-foreground/70`,

          formButtonPrimary:
            "bg-primary text-primary-foreground hover:bg-primary/90 font-medium",

          socialButtonsBlockButton:
            `bg-[${colors.background}] border-2 border-[${colors.border}] text-[${colors.foreground}] hover:bg-[${colors.primaryLight}]/10`,
          socialButtonsBlockButtonText: `text-[${colors.foreground}] font-medium`,

          dividerLine: `bg-[${colors.border}]`,
          dividerText: `text-[${colors.foregroundMuted}]`,

          formFieldLabel: `text-foreground font-medium`,
          formFieldInput:
            `bg-white border-2 border-gray-200 text-gray-900 focus:border-rose-600 rounded-lg`,

          footerActionLink: "hidden",
          footerActionText: "hidden",
          footer: "hidden",

          userPreviewMainIdentifier: `text-foreground`,
          userPreviewSecondaryIdentifier: `text-muted-foreground`,
          userButtonPopoverCard: `bg-card border-2 border-border`,
          userButtonPopoverActionButton:
            `text-foreground hover:bg-accent`,
          userButtonPopoverActionButtonIcon: `text-primary`,
          userButtonPopoverActionButtonText: `text-foreground`,
          userButtonPopoverFooter: "hidden",

          // Hide Clerk branding
          logoBox: "hidden",
          logoImage: "hidden",
          badge: "hidden",
          identityPreviewText: "text-foreground",
          identityPreviewEditButton: "text-foreground",
        },
        layout: {
          socialButtonsIconButton: "w-8 h-8",
          socialButtonsBlockButton: "flex-1",
          userButtonBox: "w-10 h-10",
          shimmer: false,
          showOptionalFields: false,
          privacyPageUrl: undefined,
          termsPageUrl: undefined,
          logoPlacement: "none" as any,
        },
        options: {
          branding: {
            show: false,
          },
        } as any,
      }}
    >
      <SessionMonitor>{children}</SessionMonitor>
    </BaseClerkProvider>
  );
}